import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import ChangePasswordModal from './ChangePasswordModal';

const UserProfilePanel = ({ selectedUser, onUserUpdate }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  useEffect(() => {
    setIsEditing(false);
    setEditForm({});
    setIsPasswordModalOpen(false);
  }, [selectedUser?.id]);

  const mockAuditLog = [
    {
      id: 'audit-1',
      action: 'User Login',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      details: 'Successful login from mobile device',
      ipAddress: '192.168.1.100',
      userAgent: 'ARCOVerify Mobile App v2.1.0'
    },
    {
      id: 'audit-2',
      action: 'Inspection Completed',
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
      details: 'Completed inspection for Equipment #EQ-2024-0156',
      ipAddress: '192.168.1.100',
      userAgent: 'ARCOVerify Mobile App v2.1.0'
    },
    {
      id: 'audit-3',
      action: 'Profile Updated',
      timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      details: 'Phone number updated by administrator',
      ipAddress: '10.0.0.50',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
    },
    {
      id: 'audit-4',
      action: 'MFA Enabled',
      timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      details: 'Two-factor authentication enabled',
      ipAddress: '10.0.0.50',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
    }
  ];

  const mockEquipmentAssignments = [
    {
      id: 'eq-1',
      name: 'Safety Harness SH-001',
      type: 'PPE',
      serialNumber: 'SH-2024-001',
      status: 'COMPLIANT',
      lastInspection: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
      nextInspection: new Date(Date.now() + 350 * 24 * 60 * 60 * 1000)
    },
    {
      id: 'eq-2',
      name: 'Lifting Chain LC-045',
      type: 'LIFTING',
      serialNumber: 'LC-2024-045',
      status: 'MONITOR',
      lastInspection: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      nextInspection: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000)
    },
    {
      id: 'eq-3',
      name: 'Securing Rope SR-123',
      type: 'SECURING',
      serialNumber: 'SR-2024-123',
      status: 'COMPLIANT',
      lastInspection: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
      nextInspection: new Date(Date.now() + 360 * 24 * 60 * 60 * 1000)
    }
  ];

  const availablePermissions = [
    { id: 'inspect_equipment', label: 'Inspect Equipment', description: 'Perform equipment inspections' },
    { id: 'generate_reports', label: 'Generate Reports', description: 'Create and export inspection reports' },
    { id: 'mobile_access', label: 'Mobile Access', description: 'Access mobile inspection interface' },
    { id: 'manage_users', label: 'Manage Users', description: 'Create and modify user accounts' },
    { id: 'view_analytics', label: 'View Analytics', description: 'Access compliance analytics dashboard' },
    { id: 'manage_clients', label: 'Manage Clients', description: 'Manage client and site information' },
    { id: 'system_admin', label: 'System Administration', description: 'Full system administration access' },
    { id: 'train_inspectors', label: 'Train Inspectors', description: 'Provide training to other inspectors' },
    { id: 'quality_control', label: 'Quality Control', description: 'Review and validate inspections' },
    { id: 'client_portal', label: 'Client Portal', description: 'Access client portal features' }
  ];

  if (!selectedUser) {
    return (
      <div className="h-full bg-card border-l border-border flex items-center justify-center">
        <div className="text-center">
          <Icon name="UserCircle" size={64} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No User Selected</h3>
          <p className="text-sm text-muted-foreground">
            Select a user from the list to view their profile and details.
          </p>
        </div>
      </div>
    );
  }

  const handleEdit = () => {
    setEditForm({
      name: selectedUser?.name,
      email: selectedUser?.email,
      role: selectedUser?.role,
      department: selectedUser?.department,
      permissions: [...selectedUser?.permissions]
    });
    setIsEditing(true);
  };

  const handleSave = () => {
    onUserUpdate({ ...selectedUser, ...editForm });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditForm({});
    setIsEditing(false);
  };

  const handleEditChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLIANT': return 'text-success';
      case 'MONITOR': return 'text-warning';
      case 'NON_COMPLIANT': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })?.format(date);
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: 'User' },
    // { id: 'permissions', label: 'Permissions', icon: 'Shield' },
    // { id: 'equipment', label: 'Equipment', icon: 'Package' },
    // { id: 'activity', label: 'Activity', icon: 'Activity' }
  ];

  return (
    <div className="h-full bg-card border-l border-border flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-muted flex-shrink-0">
              <img
                src={selectedUser?.avatar}
                alt={selectedUser?.avatarAlt}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = '/assets/images/no_image.png';
                }}
              />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">{selectedUser?.name}</h3>
              <p className="text-sm text-muted-foreground">{selectedUser?.role}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {isEditing ? (
              <>
                <Button variant="outline" size="sm" onClick={handleCancel}>
                  Annuler
                </Button>
                <Button variant="default" size="sm" onClick={handleSave}>
                  Enregistrer
                </Button>
              </>
            ) : (
              <Button variant="outline" size="sm" iconName="Edit3" onClick={handleEdit}>
                Modifier
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1">
          {tabs?.map((tab) => (
            <Button
              key={tab?.id}
              variant={activeTab === tab?.id ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab(tab?.id)}
              iconName={tab?.icon}
              iconPosition="left"
              iconSize={14}
              className="text-xs"
            >
              {tab?.label}
            </Button>
          ))}
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'profile' && (
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Informations de base</h4>
              <div className="space-y-4">
                {isEditing ? (
                  <>
                    <Input
                      label="Nom complet"
                      value={editForm?.name}
                      onChange={(e) => setEditForm(prev => ({ ...prev, name: e?.target?.value }))}
                    />
                    <Input
                      label="Adresse e-mail"
                      type="email"
                      value={editForm?.email}
                      onChange={(e) => setEditForm(prev => ({ ...prev, email: e?.target?.value }))}
                    />
                    <Input
                      label="Role"
                      value={editForm?.role}
                      onChange={(e) => setEditForm(prev => ({ ...prev, role: e?.target?.value }))}
                    />
                    <Input
                      label="Département"
                      value={editForm?.department}
                      onChange={(e) => setEditForm(prev => ({ ...prev, department: e?.target?.value }))}
                    />
                  </>
                ) : (
                  <>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">E-mail</label>
                      <p className="text-sm text-foreground">{selectedUser?.email}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Département</label>
                      <p className="text-sm text-foreground">{selectedUser?.department}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Tenant</label>
                      <p className="text-sm text-foreground">{selectedUser?.tenant}</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Account Status */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Statut du compte</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Statut</span>
                  <span className={`text-sm font-medium ${
                    selectedUser?.status === 'active' ? 'text-success' : 'text-muted-foreground'
                  }`}>
                    {selectedUser?.status?.charAt(0)?.toUpperCase() + selectedUser?.status?.slice(1)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">MFA activée</span>
                  <Icon 
                    name={selectedUser?.mfaEnabled ? 'Shield' : 'ShieldOff'} 
                    size={16} 
                    className={selectedUser?.mfaEnabled ? 'text-success' : 'text-muted-foreground'} 
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Date d'inscription</span>
                  <span className="text-sm text-foreground">
                    {formatDate(selectedUser?.joinDate)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Dernière connexion</span>
                  <span className="text-sm text-foreground">
                    {formatDate(selectedUser?.lastLogin)}
                  </span>
                </div>
              </div>
            </div>

            {/* Change password */}
            <div className="border-t border-border pt-6">
              <h4 className="text-sm font-medium text-foreground mb-1">
                Mot de passe
              </h4>
              <p className="text-xs text-muted-foreground mb-3">
                Un email sera envoyé à l&apos;utilisateur après le changement.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                iconName="Key"
                iconPosition="left"
                onClick={() => setIsPasswordModalOpen(true)}
              >
                Changer le mot de passe
              </Button>
            </div>

            {/* Statistics */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Statistiques</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <p className="text-lg font-semibold text-foreground">{selectedUser?.equipmentAssigned}</p>
                  <p className="text-xs text-muted-foreground">Équipement attribué</p>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <p className="text-lg font-semibold text-foreground">{selectedUser?.inspectionsCompleted}</p>
                  <p className="text-xs text-muted-foreground">Inspections terminées</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'permissions' && (
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">Permissions des rôles</h4>
            <div className="space-y-3">
              {availablePermissions?.map((permission) => {
                const hasPermission = selectedUser?.permissions?.includes(permission?.id);
                
                return (
                  <div key={permission?.id} className="flex items-start space-x-3 p-3 rounded-lg border border-border">
                    <Checkbox
                      checked={hasPermission}
                      disabled={!isEditing}
                      onChange={(e) => {
                        if (isEditing) {
                          const newPermissions = e?.target?.checked
                            ? [...editForm?.permissions, permission?.id]
                            : editForm?.permissions?.filter(p => p !== permission?.id);
                          setEditForm(prev => ({ ...prev, permissions: newPermissions }));
                        }
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{permission?.label}</p>
                      <p className="text-xs text-muted-foreground">{permission?.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'equipment' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-foreground">Assigned Equipment</h4>
              <span className="text-xs text-muted-foreground">
                {mockEquipmentAssignments?.length} items
              </span>
            </div>
            
            <div className="space-y-3">
              {mockEquipmentAssignments?.map((equipment) => (
                <div key={equipment?.id} className="p-3 border border-border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium text-foreground">{equipment?.name}</p>
                      <p className="text-xs text-muted-foreground">{equipment?.serialNumber}</p>
                    </div>
                    <span className={`text-xs font-medium ${getStatusColor(equipment?.status)}`}>
                      {equipment?.status}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <span className="text-muted-foreground">Dernière inspection:</span>
                      <p className="text-foreground">{formatDate(equipment?.lastInspection)}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Prochaine inspection:</span>
                      <p className="text-foreground">{formatDate(equipment?.nextInspection)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">Activité récente</h4>
            
            <div className="space-y-3">
              {mockAuditLog?.map((log) => (
                <div key={log?.id} className="p-3 border border-border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-foreground">{log?.action}</p>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(log?.timestamp)}
                    </span>
                  </div>
                  
                  <p className="text-xs text-muted-foreground mb-2">{log?.details}</p>
                  
                  <div className="text-xs text-muted-foreground space-y-1">
                    <div>IP: {log?.ipAddress}</div>
                    <div className="truncate">Agent utilisateur: {log?.userAgent}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        user={selectedUser}
      />
    </div>
  );
};

export default UserProfilePanel;